import type { MouseEvent, ReactNode } from 'react';

import { cn } from '@mds/shared/lib/utils';

type Variant = 'dark' | 'mint' | 'quiet' | 'danger';

const variants: Record<Variant, string> = {
  dark: 'bg-btn-solid-bg text-btn-solid-fg hover:bg-btn-solid-hover-bg hover:text-btn-solid-hover-fg',
  mint: 'bg-btn-brand-bg text-btn-brand-fg hover:bg-btn-brand-hover-bg hover:text-btn-brand-hover-fg',
  quiet:
    'border border-btn-quiet-edge bg-btn-quiet-bg text-btn-quiet-fg hover:border-btn-quiet-hover-edge hover:text-btn-quiet-hover-fg',
  danger:
    'border border-btn-danger-edge bg-btn-danger-bg text-btn-danger-fg hover:bg-btn-danger-hover-bg hover:text-btn-danger-hover-fg',
};

interface Props {
  children: ReactNode;
  /** Handed the event, so a button inside a clickable row can stop it there. */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  variant?: Variant;
  className?: string;
  type?: 'button' | 'submit';
  disabled?: boolean;
  /** For buttons whose visible text repeats down a list, e.g. several "Remove"s. */
  'aria-label'?: string;
}

/** The app's primary control. `variant` picks the fill; height is fixed at 48px. */
export default function Button({
  children,
  onClick,
  variant = 'dark',
  className,
  type = 'button',
  disabled,
  'aria-label': ariaLabel,
}: Props) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        'inline-flex h-(--target-control) shrink-0 items-center justify-center gap-2 rounded-button px-5 text-base font-semibold whitespace-nowrap transition-all duration-(--duration-fast) ease-standard focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color) disabled:cursor-not-allowed disabled:opacity-55',
        variants[variant],
        className,
      )}
    >
      {children}
    </button>
  );
}
